import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Layout, Typography, Card, Flex, Divider, Spin, Button, message } from "antd";

import { getJwtPayload, formatDateTime } from "../../utils/appUtils";
import { RAZORPAY_KEY_ID } from "../../utils/constants";
import EventHeader from "../../components/Events/EventHeader";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import {
  selectBookedSeatsData,
  selectSelectedTables,
  generateOrder,
  bookTicket,
  updatePaymentId,
  resetBookedSeatsData,
  resetSelectedTables,
  resetBookTicket,
  resetGenerateOrder,
  resetUpdatePaymentData
} from "../../redux/orderSlice";
import { selectTicketCategories, fetchEventBySlug, clearPublicEventBySlug, selectPublicEventBySlug, selectFetchEventBySlugStatus } from "../../redux/eventSlice";

const { Content } = Layout;
const { Title, Text } = Typography;

export default function PaymentsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { state } = useLocation();

  const [event, setEvent] = useState(state?.event || null);
  const [ticketAmountData, setTicketAmountData] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [convenienceFee, setConvenienceFee] = useState(0);
  const [enablePayment, setEnablePayment] = useState(false);
  const [displayBookingSuccessMessage, setDisplayBookingSuccessMessage] = useState(false);
  const [paymentId, setPaymentId] = useState(null);

  const publicEventBySlug = useSelector(selectPublicEventBySlug);
  const slugFetchStatus = useSelector(selectFetchEventBySlugStatus);
  const bookedSeatsData = useSelector(selectBookedSeatsData);
  const selectedTables = useSelector(selectSelectedTables);
  const ticketCategories = useSelector(selectTicketCategories);

  const generateOrderData = useSelector(state => state.order.orderData);
  const generateOrderSuccess = useSelector(state => state.order.generateOrderSuccess);
  const generateOrderError = useSelector(state => state.order.generateOrderError);
  const bookTicketSuccess = useSelector(state => state.order.bookTicketSuccess);
  const bookTicketError = useSelector(state => state.order.bookTicketError);
  const updatePaymentIdData = useSelector(state => state.order.updatePaymentIdData);
  const updatePaymentIdSuccess = useSelector(state => state.order.updatePaymentIdSuccess);
  const updatePaymentIdError = useSelector(state => state.order.updatePaymentIdError);

  const token = localStorage.getItem('authToken');
  const userPayload = getJwtPayload(token);

  const userName = userPayload?.name;
  const userEmail = userPayload?.email;
  const userContact = userPayload?.sub;

  const ticketMode = event?.ticket_mode || "per_seat";
  const isPerTable = ticketMode === "per_table";

  const eventSlug = event?.slug || slug;
  const loadingStatus = event ? "completed" : slugFetchStatus;

  const currencyCode = isPerTable
    ? (event?.tables?.[0]?.currency?.currency_code || "INR")
    : (event?.tickets?.[0]?.currency?.currency_code || "INR");

  const hasBookingSelection = isPerTable
    ? selectedTables && selectedTables.length > 0
    : bookedSeatsData && Object.values(bookedSeatsData).some((qty) => qty > 0);

  useEffect(() => {
    if (!bookedSeatsData && !selectedTables && slug) {
      navigate(`/event-details/booking/${slug}`);
    }

    if (state?.event) {
      setEvent(state.event);
      return undefined;
    }

    if (!slug) return undefined;

    dispatch(clearPublicEventBySlug());
    dispatch(fetchEventBySlug(slug));

    return () => {
      dispatch(clearPublicEventBySlug());
    };
  }, [dispatch, slug, state?.event, bookedSeatsData, selectedTables, navigate]);

  useEffect(() => {
    if (!state?.event && publicEventBySlug) {
      setEvent(publicEventBySlug);
    }
  }, [publicEventBySlug, state?.event]);

  useEffect(() => {
    if (isPerTable && selectedTables?.length) {
      const subtotal = selectedTables.reduce((sum, t) => sum + (Number(t.price) || 0), 0);
      setTotalAmount(subtotal);
      setTaxAmount(event?.is_taxable && subtotal > 0 ? subtotal * 0.18 : 0);
      setConvenienceFee(event?.add_convenience_fee && subtotal > 0 ? subtotal * 0.02 : 0);
      return;
    }

    if (event?.tickets && event?.tickets.length > 0 && bookedSeatsData) {
      const amountData = {};
      event.tickets.forEach((ticket) => {
        if (bookedSeatsData[ticket.ticket_category_id]) {
          amountData[ticket.ticket_category_id] = ticket.price * bookedSeatsData[ticket.ticket_category_id];
        } else {
          amountData[ticket.ticket_category_id] = 0;
        }
      });
      const subtotal = Object.values(amountData).reduce((acc, curr) => acc + curr, 0);
      setTicketAmountData({ ...amountData });
      setTotalAmount(subtotal);
      setTaxAmount(event?.is_taxable && subtotal > 0 ? subtotal * 0.18 : 0);
      setConvenienceFee(event?.add_convenience_fee && subtotal > 0 ? subtotal * 0.02 : 0);
    }
  }, [event, bookedSeatsData, selectedTables, isPerTable]);

  useEffect(() => {
    if (event && hasBookingSelection && totalAmount > 0 && !generateOrderData && !generateOrderError) {
      const orderPayload = {
        user_id: userPayload?.id,
        event_id: event?.id,
        convenience_fee: parseFloat(convenienceFee),
        tax_amount: parseFloat(taxAmount),
        total_amount: parseFloat(totalAmount + taxAmount),
        currency: currencyCode,
      };

      if (isPerTable && selectedTables?.length) {
        orderPayload.selected_tables = selectedTables.map((t) => ({
          event_table_id: t.event_table_id,
          table_number: t.table_number,
        }));
      }

      dispatch(generateOrder(orderPayload));
    }
  }, [
    hasBookingSelection,
    bookedSeatsData,
    selectedTables,
    event,
    userPayload,
    convenienceFee,
    taxAmount,
    totalAmount,
    generateOrderData,
    generateOrderError,
    isPerTable,
    currencyCode,
    dispatch,
  ]);

  useEffect(() => {
    if (generateOrderSuccess) {
      setEnablePayment(true);
    } else if (generateOrderError) {
      message.error(generateOrderError);
    }
  }, [generateOrderSuccess, generateOrderError, generateOrderData]);

  useEffect(() => {
    if (paymentId && !updatePaymentIdData && (bookTicketSuccess || bookTicketError)) {
      dispatch(updatePaymentId({
        order_id: generateOrderData?.order_id,
        payment_id: paymentId,
        booking_status: bookTicketSuccess ? "Confirmed" : "Declined"
      }));
    }
  }, [bookTicketSuccess, bookTicketError, paymentId, updatePaymentIdData, dispatch, generateOrderData?.order_id]);

  useEffect(() => {
    if (!displayBookingSuccessMessage) return undefined;

    const paymentPath = `/event-details/payment/${eventSlug}`;
    const lockHref = `${window.location.origin}${paymentPath}${window.location.search}${window.location.hash}`;

    const seedStack = () => {
      for (let i = 0; i < 32; i += 1) {
        window.history.pushState({ pvPaymentLock: true }, "", lockHref);
      }
    };
    seedStack();

    const onPopState = () => {
      if (window.location.pathname !== paymentPath) {
        navigate(paymentPath, { replace: true });
        return;
      }
      window.history.pushState({ pvPaymentLock: true }, "", window.location.href);
    };

    window.addEventListener("popstate", onPopState, true);
    return () => window.removeEventListener("popstate", onPopState, true);
  }, [displayBookingSuccessMessage, eventSlug, navigate]);

  useEffect(() => {
    if (updatePaymentIdSuccess) {
      setDisplayBookingSuccessMessage(true);
    } else if (updatePaymentIdError) {
      message.error("Booking Failed: " + updatePaymentIdError);
    }
  }, [updatePaymentIdSuccess, updatePaymentIdError]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (orderData, userData) => {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const finalAmount = (totalAmount + taxAmount + convenienceFee) - convenienceFee;
    const orderId = orderData?.order_id;

    const paymentOptions = {
      key: RAZORPAY_KEY_ID,
      amount: finalAmount * 100,
      currency: currencyCode,
      name: "Pingvite Events",
      description: "Booking for Order ID: " + orderId,
      image: `https://pingvite.com/logo.svg`,
      order_id: `${orderId}`,
      handler: function (response) {
        setPaymentId(response.razorpay_payment_id);

        if (isPerTable) {
          dispatch(bookTicket({
            event_id: event?.id,
            user_id: userPayload?.id,
            order_id: generateOrderData?.order_id,
            booking_mode: "per_table",
            table_items: selectedTables.map((t) => ({
              event_table_id: t.event_table_id,
              table_number: t.table_number,
              actual_amount: t.price,
            })),
          }));
        } else {
          dispatch(bookTicket({
            event_id: event?.id,
            user_id: userPayload?.id,
            order_id: generateOrderData?.order_id,
            booking_mode: "per_seat",
            ticket_category: Object.keys(bookedSeatsData).filter(key => bookedSeatsData[key] > 0).map((key) => ({
              id: parseInt(key, 10),
              no_of_seats: bookedSeatsData[key],
              actual_amount: ticketAmountData[key]
            })),
          }));
        }
      },
      prefill: userData,
      notes: {
        address: "Pingvite Corporate Office, Kolkata, India"
      },
      theme: {
        color: "#61dafb"
      }
    };

    if (typeof window.Razorpay !== 'undefined') {
      const rzp = new window.Razorpay(paymentOptions);
      rzp.open();
    } else {
      alert("Error: Razorpay object not found. Please refresh.");
    }
  };

  const venueName = event?.venue?.name || "Venue Name";
  const startDateTime = formatDateTime(event?.event_start_date_time);

  if (loadingStatus === "loading") {
    return (
      <>
        <Header isHome={false}/>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" tip="Loading payment page..." />
        </Content>
        <Footer />
      </>
    );
  }

  return (
    <>
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai"
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability,
          and city-specific visibility for organizers and users."
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata,
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai,
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai,
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />

      {!displayBookingSuccessMessage && <Header isHome={false}/>}

      <Content className="topheaderLayout">
        <EventHeader
          event={event}
          customizedButtons={enablePayment && !displayBookingSuccessMessage ? [
            {
              text: "Pay Now",
              onClick: () => handlePayment(generateOrderData, {
                name: userName,
                email: userEmail,
                contact: userContact
              }),
              type: "primary",
              className: "btnGradient"
            }
          ] : []}
        />
        <Flex justify="center" align="center" vertical>
          {displayBookingSuccessMessage && (
            <div className="bookingSuccessScreen">
              <div className="bookingSuccessMessage">
                <Title level={2}>Booking Successful</Title>
                <Text>Thank you for your booking. Your {isPerTable ? "table" : "ticket"} has been booked successfully.</Text>
                <div className="bookingSuccessTransactionId">
                  <Text>
                    Transaction ID: <Text strong>{paymentId}</Text>
                  </Text>
                </div>
                <div>
                  <Button type="primary" className="btnGradient bookingSuccessHomeBtn" onClick={() => {
                    dispatch(resetUpdatePaymentData());
                    dispatch(resetBookTicket());
                    dispatch(resetGenerateOrder());
                    dispatch(resetBookedSeatsData());
                    dispatch(resetSelectedTables());
                    navigate('/')
                  }}>Home</Button>
                </div>
              </div>
            </div>
          )}
          <Card className="paymentSummaryCard">
            {isPerTable && selectedTables?.map((table) => (
              <div key={`${table.event_table_id}-${table.table_number}`}>
                <Flex justify="space-between" align="start" className="orderItem">
                  <div>
                    <Title level={4}>{table.label} — Table #{table.table_number}</Title>
                    <div className="orderMeta">
                      <Text>{table.persons_count} persons</Text>
                      <Text>{venueName}</Text>
                      <Text>{startDateTime}</Text>
                    </div>
                  </div>
                  <Text className="orderPrice">{table.price}</Text>
                </Flex>
                <Divider />
              </div>
            ))}

            {!isPerTable && event?.tickets && event?.tickets.length > 0 && event?.tickets.map((ticket) => (
              bookedSeatsData?.[ticket.ticket_category_id] > 0 && (
                <div key={ticket.id || ticket.ticket_category_id}>
                  <Flex justify="space-between" align="start" className="orderItem">
                    <div>
                      <Title level={4}>
                        {ticketCategories && ticketCategories.length > 0 ?
                          `${ticketCategories.find(category => category.id === ticket?.ticket_category_id)?.category_name} Pass`
                          :
                          "Free Pass"
                        }
                      </Title>
                      <div className="orderMeta">
                        <Text>{venueName}</Text>
                        <Text>{startDateTime}</Text>
                        <Flex justify="space-between" align="center" gap={10}>
                          <Text>Quantity: {bookedSeatsData[ticket.ticket_category_id]}</Text>
                        </Flex>
                      </div>
                    </div>
                    <Text className="orderPrice">{ticketAmountData?.[ticket?.ticket_category_id]}</Text>
                  </Flex>
                  <Divider />
                </div>
              )
            ))}

            {event?.is_taxable && (
              <Flex justify="space-between" className="orderRow">
                <Text>Taxes</Text>
                <Text strong>{taxAmount}</Text>
              </Flex>
            )}
            {event?.add_convenience_fee && (
              <Flex justify="space-between" className="orderRow">
                <Text>Convenience Fee (2%)</Text>
                <Text strong>{convenienceFee}</Text>
              </Flex>
            )}
            {event?.add_convenience_fee && (
              <Flex justify="space-between" className="orderRow">
                <Text>Discount</Text>
                <Text strong>{convenienceFee}</Text>
              </Flex>
            )}

            <Flex justify="space-between" className="orderTotal">
              <Text strong>Total</Text>
              <Text strong className="totalPrice">
                {currencyCode} {(totalAmount + taxAmount + convenienceFee) - convenienceFee}
              </Text>
            </Flex>
          </Card>
        </Flex>
      </Content>

      {!displayBookingSuccessMessage && <Footer />}
    </>
  );
}
